import { queryKeys } from '@mevaser/shared'
import { Badge, humanizeStatus, SUPPORT_TICKET_STATUS_TONE } from '@mevaser/ui'
import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { MessageSquare } from 'lucide-react'
import { useState } from 'react'
import { api } from '../lib/api.js'
import { useAuth } from '../lib/auth.js'
import {
  MessageBubble,
  type SupportTicketDetail,
  type SupportTicketMessage,
} from '../routes/support-ticket-detail.js'

/**
 * Read-only view of a support ticket's conversation (subject, original message,
 * replies and internal notes), shown on a follow-up that came from that ticket
 * (t1370). Fetched live, never copied onto the task. Shares query keys with the
 * ticket page, so it is usually already cached.
 */
export function TicketThread({
  ticketId,
  preview = 5,
}: {
  ticketId: string
  /** How many of the latest messages show before "Show all". */
  preview?: number
}) {
  const { can } = useAuth()
  const canRead = can('support:read')
  const [showAll, setShowAll] = useState(false)

  const { data: ticket } = useQuery({
    queryKey: queryKeys.supportTickets.detail(ticketId),
    queryFn: () => api.get<SupportTicketDetail>(`/support-tickets/${ticketId}`),
    enabled: canRead,
  })
  const { data: messages, isLoading } = useQuery({
    queryKey: queryKeys.supportTickets.messages(ticketId),
    queryFn: () => api.get<SupportTicketMessage[]>(`/support-tickets/${ticketId}/messages`),
    enabled: canRead,
  })

  const all = messages ?? []
  const visible = showAll ? all : all.slice(-preview)
  const hidden = all.length - visible.length

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-foreground-muted">
        <MessageSquare className="size-3.5" aria-hidden="true" />
        <span>From ticket</span>
        <Link
          to="/support-tickets/$ticketId"
          params={{ ticketId }}
          className="font-identifier font-medium text-primary hover:underline"
        >
          {ticket?.ticketNumber ?? 'ticket'}
        </Link>
        {ticket ? (
          <>
            <Badge tone={SUPPORT_TICKET_STATUS_TONE[ticket.status] ?? 'neutral'}>
              {humanizeStatus(ticket.status)}
            </Badge>
            {ticket.customerName ? <span>· {ticket.customerName}</span> : null}
          </>
        ) : null}
      </div>
      {ticket ? <p className="text-sm font-medium">{ticket.subject}</p> : null}
      {!canRead ? null : isLoading ? (
        <p className="text-xs text-foreground-muted">Loading the conversation…</p>
      ) : all.length === 0 ? (
        <p className="text-xs text-foreground-muted">Nothing on the ticket yet.</p>
      ) : (
        <>
          {hidden > 0 ? (
            <button
              type="button"
              className="text-xs text-primary hover:underline"
              onClick={() => setShowAll(true)}
            >
              Show all {all.length} messages
            </button>
          ) : null}
          <div className="max-h-80 space-y-2 overflow-y-auto scrollbar-thin">
            {visible.map((m) => (
              <MessageBubble key={m.id} ticketId={ticketId} message={m} />
            ))}
          </div>
        </>
      )}
    </div>
  )
}
